import { FormEvent, useState } from "react";
import { Mail, Phone, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { contactApi } from "@/lib/api/contact";
import { MARKETING_CONTACT } from "./marketing-content";

export function MarketingContact() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [statusMessage, setStatusMessage] = useState("");

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setIsSubmitting(true);
    setStatus("idle");
    setStatusMessage("");

    try {
      const result = await contactApi.submit({ name, email, message });
      setStatus("success");
      setStatusMessage(result.message);
      setName("");
      setEmail("");
      setMessage("");
    } catch (error: any) {
      setStatus("error");
      setStatusMessage(
        error.response?.data?.message ||
          "Failed to send your message. Please try again or email us directly."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="container mx-auto px-4 py-20">
      <div className="text-center mb-12">
        <h2 className="text-3xl md:text-4xl font-bold mb-4">Contact us</h2>
        <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
          Want to use PhiloLMS or learn more? Send a message and we will get back to you.
        </p>
      </div>

      <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-8">
        <div className="p-8 rounded-lg border bg-card">
          <h3 className="text-xl font-semibold mb-3">Get in touch</h3>
          <p className="text-muted-foreground mb-8">
            Reach the PhiloLMS team for demos, onboarding questions, and partnership inquiries.
          </p>
          <div className="space-y-6">
            <a href={`mailto:${MARKETING_CONTACT.email}`} className="flex items-start gap-3 group">
              <span className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                <Mail className="h-5 w-5 text-primary" />
              </span>
              <span>
                <span className="block text-sm text-muted-foreground">Email</span>
                <span className="font-medium group-hover:text-primary transition-colors">
                  {MARKETING_CONTACT.email}
                </span>
              </span>
            </a>
            <a href={`tel:${MARKETING_CONTACT.phoneTel}`} className="flex items-start gap-3 group">
              <span className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                <Phone className="h-5 w-5 text-primary" />
              </span>
              <span>
                <span className="block text-sm text-muted-foreground">Phone</span>
                <span className="font-medium group-hover:text-primary transition-colors">
                  {MARKETING_CONTACT.phoneDisplay}
                </span>
              </span>
            </a>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-8 rounded-lg border bg-card space-y-5">
          <div className="space-y-2">
            <Label htmlFor="marketing-name">Name</Label>
            <Input
              id="marketing-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Your name"
              required
              disabled={isSubmitting}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="marketing-email">Email</Label>
            <Input
              id="marketing-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@library.org"
              required
              disabled={isSubmitting}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="marketing-message">Message</Label>
            <Textarea
              id="marketing-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Tell us about your library and what you would like to know"
              rows={5}
              required
              disabled={isSubmitting}
            />
          </div>
          {status === "success" && (
            <p className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-md px-3 py-2">
              {statusMessage}
            </p>
          )}
          {status === "error" && (
            <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
              {statusMessage}
            </p>
          )}
          <Button type="submit" className="w-full sm:w-auto" disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Sending...
              </>
            ) : (
              "Send message"
            )}
          </Button>
        </form>
      </div>
    </section>
  );
}
